import { getSession } from '../auth';
import { toast } from '../components/toast';
import { computePayslip } from '../lib/salarySlip';
import { openModal, closeModal } from '../components/modal';
import type { Employee, PayrollSlipShape } from '../domain';

export interface PayrollPeriod { id: string; label: string; approved?: boolean; }

const KEY = 'clt-payroll-periods';
const EMPLOYEES: Employee[] = []; // production roster arrives through staff records

function readPeriods(): PayrollPeriod[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
function writePeriods(p: PayrollPeriod[]) { localStorage.setItem(KEY, JSON.stringify(p)); }

export function renderPayroll(): string {
  const session = getSession();
  const isOwner = !!session && (session.role === 'owner' || session.role === 'admin');
  const periods = readPeriods();

  // Toolbar row per period — clean, no native title tooltips, no native prompts.
  const rows = periods.map((p) => (
    '<div class="period-row" data-period-row="' + p.id + '">' +
      '<div class="period-row-info">' +
        '<span class="t-strong period-label">' + escapeHtml(p.label) + '</span>' +
        '<span class="pill ' + (p.approved ? 'pill-success' : 'pill-info') + '">' + (p.approved ? 'approved' : 'draft') + '</span>' +
      '</div>' +
      (isOwner ? '<div class="period-row-actions">' +
        '<button type="button" class="icon-btn" data-period-rename="' + p.id + '" aria-label="Rename period">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>' +
        '</button>' +
        '<button type="button" class="icon-btn icon-btn-danger" data-period-del="' + p.id + '" aria-label="Delete period">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>' +
        '</button>' +
      '</div>' : '') +
    '</div>'
  )).join('');

  const preview = periods[0] && EMPLOYEES[0] ? renderPreview() : '';
  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Payroll</div><h1 class="t-h1">Payroll</h1>' +
    '<p class="t-meta">' + (EMPLOYEES.length + ' staff on record · ' + periods.length + ' periods') + '</p></div>' +
    (isOwner ? '<button class="btn btn-accent touch-target" data-action="payroll-new-period">+ New period</button>' : '') + '</div>' +
    '<div class="card card-pad-lg" style="margin-top:12px"><div class="t-eyebrow" style="margin-bottom:10px">Periods</div>' +
    (rows || '<div class="t-meta">No payroll periods yet. Owners create a period, approve it, and payslips are generated.</div>') + '</div>' +
    (preview ? preview : '<div class="card card-pad-lg empty" style="margin-top:16px"><div class="empty-illus">₹</div><h2>No payslips to preview</h2><p style="font-size:var(--t-sm)">Once staff hours are recorded and a period is approved, payslips appear here.</p></div>')
  );
}

function renderPreview(): string {
  const slip = computePayslip(EMPLOYEES[0], ['2026-09-01']);
  return '<div class="card card-pad-lg" style="margin-top:16px"><div class="t-eyebrow">Preview</div><div class="row"><div style="flex:1"><div class="t-strong">Gross</div><div class="t-mono">₹' + slip.grossTotal + '</div></div><div style="flex:1"><div class="t-strong">Net pay</div><div class="t-mono">₹' + slip.net + '</div></div></div></div>';
}

export function bindPayroll(): void {
  document.addEventListener('click', (e) => {
    const del = (e.target as HTMLElement).closest<HTMLElement>('[data-period-del]');
    if (del && del.dataset.periodDel) {
      const id = del.dataset.periodDel;
      const label = readPeriods().find((x) => x.id === id)?.label || 'this period';
      openConfirmDelete(id, label);
      return;
    }
    const ren = (e.target as HTMLElement).closest<HTMLElement>('[data-period-rename]');
    if (ren && ren.dataset.periodRename) {
      const id = ren.dataset.periodRename;
      const cur = readPeriods().find((x) => x.id === id);
      if (!cur) return;
      openRenameModal(id, cur.label);
      return;
    }
    const btn = (e.target as HTMLElement).closest('[data-action="payroll-new-period"]');
    if (!btn) return;
    const session = getSession();
    const label = 'W' + (readPeriods().length + 1) + ' · ' + new Date().toISOString().slice(0, 10);
    const next: PayrollPeriod = { id: 'per-' + Date.now(), label };
    writePeriods([...readPeriods(), next]);
    toast('Period ' + label + ' created as draft', 'success');
    (window as any).__rerender?.();
  });
}

function openRenameModal(id: string, currentLabel: string): void {
  openModal({
    id: 'payroll-rename',
    title: 'Rename payroll period',
    subtitle: 'Change the label your team will see in the periods list.',
    width: 480,
    html:
      '<form id="payroll-rename-form">' +
        '<label class="t-eyebrow">Period label' +
          '<input name="label" required maxlength="64" value="' + escapeAttr(currentLabel) + '" class="form-input" placeholder="e.g. September 2026 · Week 1"/>' +
        '</label>' +
        '<div class="row" style="margin-top:24px;gap:10px;justify-content:flex-end">' +
          '<button class="btn btn-ghost" type="button" data-modal-close>Cancel</button>' +
          '<button class="btn btn-primary" type="submit">Save new label</button>' +
        '</div>' +
      '</form>',
    onSubmit: (form, close) => {
      const fd = new FormData(form);
      const next = String(fd.get('label') || '').trim();
      if (!next) { toast('Label cannot be empty', 'warn'); return; }
      writePeriods(readPeriods().map((x) => x.id === id ? { ...x, label: next } : x));
      toast('Period renamed', 'success');
      (window as any).__rerender?.();
      close();
    },
  });
}

function openConfirmDelete(id: string, label: string): void {
  openModal({
    id: 'payroll-delete',
    title: 'Delete payroll period?',
    subtitle: 'This will permanently remove "' + label + '" from the periods list.',
    width: 460,
    html:
      '<p class="t-meta" style="margin-bottom:20px">Payslips under this period will be detached. This action cannot be undone.</p>' +
      '<div class="row" style="gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-modal-close>Cancel</button>' +
        '<button class="btn btn-danger" type="button" data-confirm-delete>Delete period</button>' +
      '</div>',
    onMount: (host, close) => {
      const btn = host.querySelector<HTMLButtonElement>('[data-confirm-delete]');
      if (btn) btn.addEventListener('click', () => {
        writePeriods(readPeriods().filter((x) => x.id !== id));
        toast('Period deleted', 'success');
        (window as any).__rerender?.();
        close();
      });
    },
  });
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function escapeAttr(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}